import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface MediaItem {
  id: number;
  type: 'image' | 'video';
  url: string;
  alt: string;
  thumbnail?: string;
}

interface PetGalleryProps {
  media: MediaItem[];
  petName: string;
}

const PetGallery = ({ media, petName }: PetGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const selectedMedia = media[selectedIndex];

  // Prevent scroll when lightbox is open
  React.useEffect(() => {
    if (showLightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLightbox]);

  return (
    <div className="space-y-4">
      {/* Main Display Area */}
      <div className="relative bg-muted rounded-2xl overflow-hidden aspect-[4/3] group">
        {selectedMedia.type === 'image' ? (
          <div className="relative w-full h-full cursor-pointer" onClick={() => setShowLightbox(true)}>
            <AppImage
              src={selectedMedia.url}
              alt={selectedMedia.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Icon name="MagnifyingGlassPlusIcon" size={48} className="text-white drop-shadow-lg" />
            </div>
          </div>
        ) : (
          <video
            src={selectedMedia.url}
            controls
            className="w-full h-full object-cover"
            aria-label={`Video of ${petName}`}
          >
            Your browser does not support the video tag.
          </video>
        )}

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 rounded-full hover:bg-white transition-all shadow-lg hover:scale-110 z-10"
              aria-label="Previous media"
            >
              <Icon name="ChevronLeftIcon" size={24} variant="solid" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 rounded-full hover:bg-white transition-all shadow-lg hover:scale-110 z-10"
              aria-label="Next media"
            >
              <Icon name="ChevronRightIcon" size={24} variant="solid" />
            </button>
          </>
        )}

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 text-white rounded-full text-sm font-medium z-10">
          {selectedIndex + 1} / {media.length}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
        {media.map((item, index) => (
          <button
            key={item.id}
            onClick={() => setSelectedIndex(index)}
            className={`relative aspect-square rounded-lg overflow-hidden transition-all ${index === selectedIndex
                ? 'ring-4 ring-primary scale-105'
                : 'hover:scale-105 opacity-70 hover:opacity-100'
              }`}
            aria-label={`View ${item.type} ${index + 1}`}
          >
            <AppImage
              src={item.thumbnail || item.url}
              alt={`Thumbnail ${index + 1} of ${petName}`}
              className="w-full h-full object-cover"
            />
            {item.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Icon name="PlayIcon" size={20} variant="solid" className="text-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors"
            onClick={() => setShowLightbox(false)}
          >
            <Icon name="XMarkIcon" size={32} />
          </button>

          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
            {selectedMedia.type === 'image' ? (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.alt}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-full"
              />
            )}

            {/* Lightbox Navigation */}
            {media.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevious();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-4 text-white/70 hover:text-white transition-colors hover:scale-110"
                >
                  <Icon name="ChevronLeftIcon" size={48} variant="solid" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-4 text-white/70 hover:text-white transition-colors hover:scale-110"
                >
                  <Icon name="ChevronRightIcon" size={48} variant="solid" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PetGallery;